import React, { useEffect, useRef, useState } from "react";
import { api, API, apiError, TOKEN_KEY } from "../lib/api";
import {uploadWithRecovery} from '../lib/reliableOperations';
import {captureSession,apiSessionScope} from '../lib/sessionScope';
import { toast } from "sonner";
import { Button } from "./ui/button";
import { Upload, X, Loader2, ImagePlus, Camera } from "lucide-react";
import { ProtectedFile } from "./ProtectedFile";

export function PhotoUploader({ value = [], onChange, label = "Foto e allegati", accept = "image/png,image/jpeg,application/pdf", readOnly = false, disabled = false, useCamera = false, onBusyChange }) {
  const inputRef = useRef();
  const cameraRef = useRef();
  const inFlight = useRef(false);
  const mounted = useRef(true), operation = useRef(null);
  useEffect(() => {
    mounted.current = true;
    return () => {mounted.current = false;operation.current?.abort();};
  }, []);
  const setWorking = value => {inFlight.current=value;setBusy(value);onBusyChange?.(value);};
  const [busy, setBusy] = useState(false);
  const [uploadError,setUploadError] = useState("");

  const handleFiles = async (files) => {
    if (readOnly || disabled || inFlight.current || !files?.length) return;
    setWorking(true);
    setUploadError("");
    const session = captureSession(TOKEN_KEY), controller = new AbortController();
    operation.current = controller;
    const current = () => mounted.current && !controller.signal.aborted && session.isCurrent();
    const added = [];
    const failed = [];
    for (const f of Array.from(files)) {
      if (!current()) return;
      if (f.size > 8 * 1024 * 1024) {failed.push(`${f.name} (massimo 8 MiB per file)`);continue;}
      try {
        const { data } = await uploadWithRecovery(api,API,f,{signal:controller.signal, headers: { "Content-Type": "multipart/form-data" } });
        if (!current()) return;
        added.push({ path: data.path, filename: data.filename, content_type: data.content_type });
      } catch (e) {
        if (!current()) return;
        failed.push(`${f.name} (${apiError(e)})`);
      }
    }
    if (!current()) return;
    if(failed.length)setUploadError(`Allegati da verificare: ${failed.join(", ")}. Dopo la verifica puoi selezionare di nuovo gli stessi file per recuperarli.`);
    if (added.length) {
      try {
        const known=new Set((value||[]).map(item=>item.path));
        await onChange([...(value || []), ...added.filter(item=>!known.has(item.path))]);
        if (current()) toast.success(`${added.length} file caricati`);
      } catch (e) { if (current()) toast.error(e.operationState?apiError(e):"Salvataggio degli allegati fallito. Riprova."); }
    }
    if (!current()) return;
    setWorking(false);
    if (inputRef.current) inputRef.current.value = "";
    if (cameraRef.current) cameraRef.current.value = "";
  };

  const remove = async (i) => {
    if (readOnly || disabled || inFlight.current) return;
    setWorking(true);
    try { await onChange(value.filter((_, idx) => idx !== i)); }
    catch (e) { toast.error(e.operationState?apiError(e):"Rimozione dell’allegato fallita. Riprova."); }
    finally { setWorking(false); }
  };

  return (
    <div>
      <div className="mb-2 flex flex-wrap items-center justify-between gap-3">
        <span className="text-sm font-medium">{label}</span>
        {!readOnly && <div className="flex flex-wrap gap-2"><Button type="button" variant="outline" size="sm" onClick={() => inputRef.current?.click()} disabled={busy || disabled} data-testid="photo-upload-btn">
          {busy ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Upload className="mr-2 h-4 w-4" />}Carica
        </Button>
        <input ref={inputRef} type="file" accept={accept.replace("image/*", "image/png,image/jpeg")} multiple disabled={busy || disabled} className="hidden" onChange={(e) => handleFiles(e.target.files)} data-testid="photo-input" />
        {useCamera && <><Button type="button" variant="outline" disabled={busy||disabled} onClick={()=>cameraRef.current?.click()}><Camera className="mr-2 h-4 w-4" aria-hidden="true"/>Scatta foto</Button><input ref={cameraRef} type="file" accept="image/png,image/jpeg" capture="environment" className="hidden" disabled={busy||disabled} onChange={e=>handleFiles(e.target.files)} data-testid="camera-input"/></>}
        </div>}
      </div>
      {uploadError && <p role="alert" className="mb-3 text-sm text-red-300">{uploadError}</p>}
      {busy && <p role="status" className="mb-3 text-sm text-muted-foreground">Caricamento allegati in corso…</p>}
      {(!value || value.length === 0) ? (
        readOnly ? <p className="py-8 text-center text-sm text-muted-foreground">Nessun file.</p> : <button type="button" disabled={busy || disabled} onClick={() => inputRef.current?.click()} className="flex w-full flex-col items-center justify-center gap-1 rounded-lg border border-dashed border-border py-8 text-muted-foreground transition-colors hover:border-primary/50 hover:text-foreground">
          <ImagePlus className="h-7 w-7" /><span className="text-sm">Nessun file. Clicca per caricare.</span>
        </button>
      ) : (
        <div className="grid grid-cols-2 gap-3 sm:grid-cols-3">
          {value.map((f, i) => {
            return (
              <div key={i} className="group relative overflow-hidden rounded-lg border border-border bg-secondary/30" data-testid={`photo-item-${i}`}>
                <ProtectedFile file={f}/>
                {!readOnly && <button type="button" disabled={busy || disabled} aria-label={`Rimuovi ${f.filename}`} onClick={() => remove(i)} className="absolute right-1 top-1 flex h-11 w-11 items-center justify-center rounded-full bg-black/80 text-white focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2" data-testid={`photo-remove-${i}`}>
                  <X className="h-5 w-5" aria-hidden="true" />
                </button>}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
